import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Layout, Spin, App } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { HomeContainer, PageHeader, PageSubtitle, PageTitle } from '../Home/styles';
import { CustomerGuide, downloadCustomerGuide, getCustomerGuide } from '../../services/docsApi';

const { Content } = Layout;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 991px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Toc = styled.nav`
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  font-size: 13px;
  a {
    display: block;
    padding: 4px 8px;
    color: #848E9C;
    border-left: 2px solid transparent;
    text-decoration: none;
  }
  a.sub { padding-left: 20px; font-size: 12px; }
  a:hover { color: #EAECEF; border-left-color: #0ECB81; }
  @media (max-width: 991px) { display: none; }
`;

// The guide is pandoc output from the .docx: plain headings, paragraphs, lists, tables, figures.
const Article = styled.article`
  background: #1E2329;
  border: 1px solid #2B3139;
  border-radius: 8px;
  padding: 24px 32px;
  color: #EAECEF;
  font-size: 14px;
  line-height: 1.65;
  min-width: 0;
  @media (max-width: 991px) { padding: 16px; }
  h1 { font-size: 22px; margin: 32px 0 12px; scroll-margin-top: 16px; }
  h1:first-child { margin-top: 0; }
  h2 { font-size: 17px; margin: 24px 0 8px; scroll-margin-top: 16px; }
  h3 { font-size: 15px; margin: 18px 0 6px; }
  p, li { color: #C9CED6; }
  /* The app's global reset strips list indentation; the guide's numbered steps need it back. */
  ol, ul { padding-left: 28px; margin: 8px 0 12px; }
  ol { list-style: decimal; }
  ul { list-style: disc; }
  li { margin: 4px 0; padding-left: 4px; }
  li > p { margin: 0; }
  strong { color: #EAECEF; }
  a { color: #0ECB81; }
  img { display: block; max-width: 100%; height: auto; margin: 12px auto 4px; border-radius: 6px; border: 1px solid #2B3139; }
  figure { margin: 16px 0; }
  figcaption, p > em:only-child { display: block; text-align: center; color: #848E9C; font-size: 12px; }
  blockquote { margin: 12px 0; padding: 8px 14px; border-left: 3px solid #F0B90B; background: rgba(240, 185, 11, 0.06); }
  blockquote p { margin: 0; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; display: block; overflow-x: auto; }
  th, td { border: 1px solid #2B3139; padding: 6px 10px; text-align: left; vertical-align: top; }
  th { background: #2B3139; color: #EAECEF; }
`;

interface Heading { id: string; text: string; level: number }

/** The element that actually scrolls here: the layout's box, the body, or the document. */
function scrollParent(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if ((overflowY === 'auto' || overflowY === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

/** Jumps straight to a heading. Smooth scrolling over a long guide stalled or stopped short. */
function jumpTo(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  const box = scrollParent(target);
  const offset = target.getBoundingClientRect().top - (box === document.scrollingElement ? 0 : box.getBoundingClientRect().top);
  box.scrollTop += offset - 16;
}

const DocsPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { message } = App.useApp();
  const [guide, setGuide] = useState<CustomerGuide | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    getCustomerGuide()
      .then(setGuide)
      .catch((e) => setError(e?.response?.data?.message || 'Failed to load the guide'));
  }, [isAuthenticated]);

  // Ids for the headings pandoc emitted, so the contents list can jump to them.
  useEffect(() => {
    const root = articleRef.current;
    if (!root || !guide) return;
    const found: Heading[] = [];
    root.querySelectorAll('h1, h2').forEach((el, i) => {
      if (!el.id) el.id = `guide-${i}`;
      found.push({ id: el.id, text: el.textContent ?? '', level: el.tagName === 'H1' ? 1 : 2 });
    });
    setHeadings(found);
  }, [guide]);

  const html = useMemo(() => ({ __html: guide?.html ?? '' }), [guide]);

  if (!isAuthenticated) return <Navigate to="/signin" replace />;

  const download = async () => {
    setDownloading(true);
    try {
      await downloadCustomerGuide();
    } catch {
      message.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <HomeContainer>
      <Content style={{ padding: 16 }}>
        <PageHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <PageTitle>Docs</PageTitle>
            <PageSubtitle>How to set up your accounts, run Grid and Pair bots, and deposit or withdraw capital</PageSubtitle>
          </div>
          <Button icon={<DownloadOutlined />} loading={downloading} onClick={download} disabled={!guide}>
            Download .docx
          </Button>
        </PageHeader>

        {error ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#F6465D' }}>{error}</div>
        ) : !guide ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
        ) : (
          <Columns>
            <Toc aria-label="Contents">
              {headings.map((h) => (
                <a key={h.id} href={`#${h.id}`} className={h.level === 2 ? 'sub' : undefined}
                  onClick={(e) => { e.preventDefault(); jumpTo(h.id); }}>
                  {h.text}
                </a>
              ))}
            </Toc>
            {/* Trusted content: built from our own guide by the BE's build script. */}
            <Article ref={articleRef} dangerouslySetInnerHTML={html} />
          </Columns>
        )}
      </Content>
    </HomeContainer>
  );
};

export default DocsPage;
